export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export enum LogCategory {
  QUEUE = 'QUEUE',
  REQUEST = 'REQUEST',
  IMAGE_SERVICE = 'IMAGE_SERVICE',
  TIME_SERIES = 'TIME_SERIES',
  SYSTEM = 'SYSTEM'
}

// Minimum time between two writes of the log buffer to localStorage.
const STORAGE_SAVE_DELAY_MS = 2000;

const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
  [LogLevel.DEBUG]: 0,
  [LogLevel.INFO]: 1,
  [LogLevel.WARN]: 2,
  [LogLevel.ERROR]: 3,
};

function parseLogLevel(value: string | undefined): LogLevel {
  switch ((value || '').toLowerCase()) {
    case 'debug': return LogLevel.DEBUG;
    case 'warn': return LogLevel.WARN;
    case 'error': return LogLevel.ERROR;
    default: return LogLevel.INFO;
  }
}

interface LogEntry {
  id: string;
  timestamp: number;
  datetime: string;
  level: LogLevel;
  category: LogCategory;
  module?: string;
  message: string;
  data?: any;
  url?: string;
}

interface LoggerConfig {
  maxEntries: number;
  enableConsole: boolean;
  enableStorage: boolean;
  minLevel: LogLevel;
  categories: LogCategory[];
}

export interface ModuleLogger {
  error(message: string, data?: any): void;
  warn(message: string, data?: any): void;
  info(message: string, data?: any): void;
  debug(message: string, data?: any): void;
}

class DebugLogger {
  private logs: LogEntry[] = [];

  private config: LoggerConfig;

  private logCounter = 0;

  private saveTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(config?: Partial<LoggerConfig>) {
    // eslint-disable-next-line no-underscore-dangle
    const env = (window as any)._env_ || {};

    this.config = {
      maxEntries: parseInt(env.REACT_APP_DEBUG_LOG_MAX_ENTRIES || '1000', 10),
      enableConsole: env.REACT_APP_DEBUG_ENABLE_CONSOLE !== 'false',
      enableStorage: env.REACT_APP_DEBUG_ENABLE_STORAGE === 'true',
      minLevel: parseLogLevel(env.REACT_APP_LOG_LEVEL),
      categories: [
        LogCategory.QUEUE,
        LogCategory.REQUEST,
        LogCategory.IMAGE_SERVICE,
        LogCategory.TIME_SERIES,
        LogCategory.SYSTEM,
      ],
      ...config,
    };

    if (this.config.enableStorage) {
      this.loadFromStorage();
      window.addEventListener('pagehide', () => this.flushToStorage());
    }
  }

  log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: any,
    url?: string,
    module?: string,
  ): void {
    if (!this.config.categories.includes(category)) {
      return;
    }

    if (LOG_LEVEL_ORDER[level] < LOG_LEVEL_ORDER[this.config.minLevel]) {
      return;
    }

    this.logCounter += 1;
    const now = new Date();

    const entry: LogEntry = {
      id: `log_${this.logCounter}_${now.getTime()}`,
      timestamp: now.getTime(),
      datetime: now.toISOString(),
      level,
      category,
      module,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : undefined,
      url: url || window.location.href,
    };

    this.logs.push(entry);
    if (this.logs.length > this.config.maxEntries) {
      this.logs.shift();
    }

    if (this.config.enableConsole) {
      DebugLogger.logToConsole(entry);
    }

    if (this.config.enableStorage) {
      this.scheduleSave();
    }
  }

  // ── Category-scoped helpers (existing API) ──────────────────────────────

  queue(level: LogLevel, message: string, data?: any): void {
    this.log(level, LogCategory.QUEUE, message, data);
  }

  request(level: LogLevel, message: string, data?: any): void {
    this.log(level, LogCategory.REQUEST, message, data);
  }

  imageService(level: LogLevel, message: string, data?: any): void {
    this.log(level, LogCategory.IMAGE_SERVICE, message, data);
  }

  timeSeries(level: LogLevel, message: string, data?: any): void {
    this.log(level, LogCategory.TIME_SERIES, message, data);
  }

  system(level: LogLevel, message: string, data?: any): void {
    this.log(level, LogCategory.SYSTEM, message, data);
  }

  // ── Module-scoped helpers (used via createLogger) ───────────────────────

  error(module: string, message: string, data?: any): void {
    this.log(LogLevel.ERROR, LogCategory.SYSTEM, message, data, undefined, module);
  }

  warn(module: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, LogCategory.SYSTEM, message, data, undefined, module);
  }

  info(module: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, LogCategory.SYSTEM, message, data, undefined, module);
  }

  debug(module: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, LogCategory.SYSTEM, message, data, undefined, module);
  }

  // ── Console output ───────────────────────────────────────────────────────

  private static logToConsole(entry: LogEntry): void {
    const emoji = DebugLogger.getEmojiForCategoryAndLevel(entry.category, entry.level);
    const label = entry.module ?? entry.category;
    const prefix = `${emoji} [${label}]`;

    switch (entry.level) {
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(prefix, entry.message, entry.data || '');
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(prefix, entry.message, entry.data || '');
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(prefix, entry.message, entry.data || '');
        break;
      default:
        // eslint-disable-next-line no-console
        console.debug(prefix, entry.message, entry.data || '');
    }
  }

  private static getEmojiForCategoryAndLevel(category: LogCategory, level: LogLevel): string {
    const emojiMap = {
      [LogCategory.QUEUE]: {
        [LogLevel.INFO]: '🔄',
        [LogLevel.WARN]: '⚠️',
        [LogLevel.ERROR]: '❌',
        [LogLevel.DEBUG]: '🔍',
      },
      [LogCategory.REQUEST]: {
        [LogLevel.INFO]: '📡',
        [LogLevel.WARN]: '⚠️',
        [LogLevel.ERROR]: '❌',
        [LogLevel.DEBUG]: '🔍',
      },
      [LogCategory.IMAGE_SERVICE]: {
        [LogLevel.INFO]: '🖼️',
        [LogLevel.WARN]: '⚠️',
        [LogLevel.ERROR]: '❌',
        [LogLevel.DEBUG]: '🔍',
      },
      [LogCategory.TIME_SERIES]: {
        [LogLevel.INFO]: '⏰',
        [LogLevel.WARN]: '⚠️',
        [LogLevel.ERROR]: '❌',
        [LogLevel.DEBUG]: '🔍',
      },
      [LogCategory.SYSTEM]: {
        [LogLevel.INFO]: '⚡',
        [LogLevel.WARN]: '⚠️',
        [LogLevel.ERROR]: '❌',
        [LogLevel.DEBUG]: '🔍',
      },
    };

    return emojiMap[category]?.[level] || '📝';
  }

  // ── Query / export ───────────────────────────────────────────────────────

  getLogs(filters?: {
    categories?: LogCategory[];
    levels?: LogLevel[];
    since?: number;
    until?: number;
    limit?: number;
  }): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (filters?.categories) {
      filteredLogs = filteredLogs.filter((log) => filters.categories!.includes(log.category));
    }

    if (filters?.levels) {
      filteredLogs = filteredLogs.filter((log) => filters.levels!.includes(log.level));
    }

    if (filters?.since) {
      filteredLogs = filteredLogs.filter((log) => log.timestamp >= filters.since!);
    }

    if (filters?.until) {
      filteredLogs = filteredLogs.filter((log) => log.timestamp <= filters.until!);
    }

    if (filters?.limit) {
      filteredLogs = filteredLogs.slice(-filters.limit);
    }

    return filteredLogs.sort((a, b) => a.timestamp - b.timestamp);
  }

  downloadLogsAsJSON(filename?: string, filters?: Parameters<typeof this.getLogs>[0]): void {
    const logs = this.getLogs(filters);
    const data = {
      meta: {
        exportedAt: new Date().toISOString(),
        totalLogs: logs.length,
        filters: filters || 'none',
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      logs,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    DebugLogger.downloadBlob(blob, filename || `debug-logs-${Date.now()}.json`);
  }

  downloadLogsAsText(filename?: string, filters?: Parameters<typeof this.getLogs>[0]): void {
    const logs = this.getLogs(filters);

    let content = 'Debug Logs Export\n';
    content += '=================\n';
    content += `Exported at: ${new Date().toISOString()}\n`;
    content += `Total logs: ${logs.length}\n`;
    content += `Filters: ${JSON.stringify(filters || 'none')}\n`;
    content += `User Agent: ${navigator.userAgent}\n`;
    content += `URL: ${window.location.href}\n\n`;

    logs.forEach((log) => {
      const label = log.module ?? log.category;
      content += `[${log.datetime}] ${log.level} [${label}] ${log.message}\n`;
      if (log.data) {
        content += `  Data: ${JSON.stringify(log.data, null, 2)}\n`;
      }
      content += '\n';
    });

    const blob = new Blob([content], { type: 'text/plain' });
    DebugLogger.downloadBlob(blob, filename || `debug-logs-${Date.now()}.txt`);
  }

  downloadLogsAsCSV(filename?: string, filters?: Parameters<typeof this.getLogs>[0]): void {
    const logs = this.getLogs(filters);

    const headers = ['ID', 'Timestamp', 'DateTime', 'Level', 'Category', 'Module', 'Message', 'Data', 'URL'];

    let csv = [headers.join(','), ''].join('\n');

    logs.forEach((log) => {
      const row = [
        log.id,
        log.timestamp,
        log.datetime,
        log.level,
        log.category,
        log.module || '',
        `"${log.message.replace(/"/g, '""')}"`,
        `"${JSON.stringify(log.data || '').replace(/"/g, '""')}"`,
        `"${log.url?.replace(/"/g, '""') || ''}"`,
      ];
      csv += [row.join(','), '\n'].join('');
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    DebugLogger.downloadBlob(blob, filename || `debug-logs-${Date.now()}.csv`);
  }

  private static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Serializing the last entries on every log call (the preload alone logs every 100 ms) is far
  // too expensive: writes are coalesced, and flushed when the page is hidden or closed.
  private scheduleSave(): void {
    if (this.saveTimer !== undefined) return;
    this.saveTimer = setTimeout(() => this.flushToStorage(), STORAGE_SAVE_DELAY_MS);
  }

  private flushToStorage(): void {
    if (this.saveTimer !== undefined) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
    }
    this.saveToStorage();
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem('debug-logs', JSON.stringify(this.logs.slice(-100)));
    } catch (error) {
      // storage full or unavailable
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('debug-logs');
      if (stored) {
        const storedLogs = JSON.parse(stored) as LogEntry[];
        this.logs = storedLogs;
        this.logCounter = Math.max(...storedLogs.map((l) => parseInt(l.id.split('_')[1] || '0', 10))) || 0;
      }
    } catch (error) {
      // error parsing or no stored logs
    }
  }

  clearLogs(): void {
    this.logs = [];
    this.logCounter = 0;
    if (this.saveTimer !== undefined) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
    }
    if (this.config.enableStorage) {
      localStorage.removeItem('debug-logs');
    }
  }

  getStats() {
    const stats = {
      totalLogs: this.logs.length,
      byCategory: {} as Record<LogCategory, number>,
      byLevel: {} as Record<LogLevel, number>,
      oldestLog: this.logs[0]?.datetime,
      newestLog: this.logs[this.logs.length - 1]?.datetime,
      memoryUsage: JSON.stringify(this.logs).length,
    };

    Object.values(LogCategory).forEach((category) => {
      stats.byCategory[category] = this.logs.filter((l) => l.category === category).length;
    });

    Object.values(LogLevel).forEach((level) => {
      stats.byLevel[level] = this.logs.filter((l) => l.level === level).length;
    });

    return stats;
  }

  exposeToWindow(): void {
    (window as any).debugLogger = {
      downloadJSON: (filters?: any) => this.downloadLogsAsJSON(undefined, filters),
      downloadText: (filters?: any) => this.downloadLogsAsText(undefined, filters),
      downloadCSV: (filters?: any) => this.downloadLogsAsCSV(undefined, filters),
      getLogs: (filters?: any) => this.getLogs(filters),
      getStats: () => this.getStats(),
      clear: () => this.clearLogs(),
    };
  }
}

export const debugLogger = new DebugLogger();

debugLogger.exposeToWindow();

export function createLogger(module: string): ModuleLogger {
  return {
    error: (message, data) => debugLogger.error(module, message, data),
    warn: (message, data) => debugLogger.warn(module, message, data),
    info: (message, data) => debugLogger.info(module, message, data),
    debug: (message, data) => debugLogger.debug(module, message, data),
  };
}

export default DebugLogger;
